import { getFontServiceParamsFromString, IFontServiceParams, FONT_SERVICE_TEXT_PARAMETER } from '../server/utils/font-service-utils';
import { CreativeDataNode, NodeKind, IImageElementDataNode, isTextNode, isWidgetNode } from './nodes';
import { Renderer, IDependency } from './renderer';
import { Animator } from './animator';
import { WidgetRenderer } from '../widget/widget-renderer';
import { IEnvironment } from '../server/environment';
import { AdEvents } from '../ad/domain/ad-events';
import { IAnimatedCreative } from './animated-creative.interface';
import { IFontStyle } from '../domain/font';
import { FeedStore } from './feed-store';
import { IFeed } from '../domain/feed';
import * as FontFaceObserver from '@bannerflow/fontfaceobserver';
import { SpanType } from '../domain/text';
import { testWebP, getImageOptimizerUrl, defaults } from '../ad/utils/image-optimizer';
import { replaceOrigin, isUrl, isRelativeUrl, concatUrl, isBannerflow } from '../ad/utils/url';
import { withinImageBounds } from '../../lib/geom';
import { Ad } from 'core/ad/ad';


declare class FontFace {
    constructor(fontFamily: string, url: string);
    load: () => Promise<FontFace>;
    display: string;
    family: string;
    featureSettings: string;
    loaded: Promise<FontFace>;
    status: string;
    stretch: string;
    style: string;
    unicodeRange: string;
    variant: string;
    weight: string;
}

interface IFontFaceset {
    add(fontFace: FontFace): void;
}

declare global {
    /* tslint:disable */
    interface Document {
        fonts: IFontFaceset;
    }
}
export interface IImagePreloadAsset {
    type: 'image';
    url: string;
}

export interface IFontPreloadAsset {
    type: 'font';
    style: IFontStyle;
}

export interface IFeedPreloadAsset {
    type: 'feed';
    id: string;
}

export type PreloadAsset = IImagePreloadAsset | IFontPreloadAsset | IFeedPreloadAsset;

interface IPreloadAssets {
    images: string[];
    fonts: IFontStyle[];
    feeds: string[];
}

export interface IDeeplinkEvent {
    event: MouseEvent;
    deepLinkUrl?: string;
}

export class AnimatedCreative implements IAnimatedCreative {

    iframe: HTMLIFrameElement;
    rootElement: HTMLElement;
    iframeWindow: Window;
    env: IEnvironment;
    animator: Animator;
    feedStore: FeedStore;
    preloadedFonts: HTMLStyleElement[] = [];
    preloadedFeeds: string[] = [];
    isWebPSupported: boolean;
    hasWidgetElements: boolean;
    private preloadAssets: IPreloadAssets = { images: [], fonts: [], feeds: [] };
    private stoppedOmniRenderRecording = false;
    private inSandboxedIframe = false;
    private document: Document;

    /**
     * The creative renderer. Note that this is shared between canvas and creatives.
     */
    private renderer: Renderer;

    constructor(
        public ad: Ad,
        private creativeData: CreativeDataNode,
        preloadAssets: PreloadAsset[],
        private onLoadCallback?: (creative?: IAnimatedCreative) => any,

        env?: IEnvironment
    ) {
        try {
            this.setEnvironments(env);
            for (const preloadAsset of preloadAssets) {
                switch (preloadAsset.type) {
                    case 'font':
                        this.preloadAssets.fonts.push(preloadAsset.style);
                        break;
                    case 'feed':
                        this.preloadAssets.feeds.push(preloadAsset.id);
                        break;
                }
            }


            this.iframe = this.createIframe();

            this.iframeWindow = this.iframe.contentWindow!;

            this.inSandboxedIframe = !this.iframe!.contentDocument;

            if (this.inSandboxedIframe) {
                this.iframe.remove();
                this.init();
            }
            else {
                // Wait for iframeWindow to be available
                this.onIframeWindowLoad(this.iframeWindow, this.init);
            }

        }
        catch (e) {
            throw new Error('Could not initialize creative: ' + e.message);
        }
    }

    /**
     * When iframe window is ready to use, init creative
     */
    private init = async () => {
        const { width, height } = this.creativeData;
        this.insertCreativeStyles();
        this.rootElement = this.getRootElement();
        this.document = this.rootElement.ownerDocument!;
        this.isWebPSupported = await testWebP();
        this.renderer = new Renderer(this.creativeData, {
            contentWindow: this.inSandboxedIframe
                ? window
                : this.iframeWindow
        });
        this.renderer.webPSupport = this.isWebPSupported;
        this.renderer.env = this.env;
        this.renderer.on('click', this.onClick);
        this.setChildUrls();
        if (FeedStore) {
            this.feedStore = new FeedStore(this.renderer, this.ad.data.brand.id);
            this.renderer.feedStore = this.feedStore;

            // Override the first feed, used for example with DCO
            if (this.ad.overriddenFeedData && this.preloadAssets.feeds.length > 0) {
                this.renderer.feedStore.feeds.set(this.preloadAssets.feeds[0], {
                    feed: {
                        data: this.ad.overriddenFeedData,
                        intervalInSeconds: 0,
                    },
                    interval: undefined,
                    overridden: true
                });
            }

            await this.preloadFeeds();
        }

        if (Animator) {
            this.animator = new Animator(this.creativeData, this.renderer);
        }

        const dependencies: IDependency[] = [];

        if (WidgetRenderer) {
            dependencies.push({
                name: 'WidgetRenderer',
                constructor: WidgetRenderer,
                arguments: [this.ad, this.renderer, this.animator]
            });
        }

        // Viewtree has to be initialized before preloading fonts for feeds
        this.renderer.render(this.rootElement, { width, height }, 0, false, dependencies, /*onlyRenderTextWithoutFontLoad*/true);

        try {
            await this.preloadFonts();

            // Note: We have to rerender to include the fonts
            // TODO: Remove this hack in the future. when we rewrite renderer to have loadAssets method.
            this.renderer.rerender(this.creativeData);
        }
        catch (e) {
            console.error('Failed to preload fonts.', e);
        }

        this.addPreloadFontsToRenderedCreative();

        await Promise.all([...this.renderer.preloadingElements.values()]);

        this.checkForWidgetElementNodes();

        if (WidgetRenderer && this.renderer['WidgetRenderer']) {
            // Ugly workaround to ensure that all widgets has completely loaded to ensure that they are in sync with the creative
            await Promise.all((this.renderer['WidgetRenderer'] as WidgetRenderer).widgetInitializingPromises);
            await Promise.all((this.renderer['WidgetRenderer'] as WidgetRenderer).widgetLoadingPromises);
        }

        if (this.ad.responsive) {
            window.addEventListener('resize', this.onResize);
            this.onResize();
        }
        this.iframe.style.opacity = '1';

        // Some times iframe is hidden (display: none), for on-site banners. Where the publisher have
        // implemented media queries to show and hide multiple banners to simulate responsiveness.
        // Hidden loaded banners have the fallback, that they have zero height when inspected, which
        // currently affects text rendering. So, we have to wait until it is shown to rerender.
        if (!this.isPixelVisible()) {
            const t = setInterval(() => {
                if (this.isPixelVisible()) {
                    this.renderer.rerender(this.creativeData, undefined, 0);
                    if (Animator) {
                        this.initPlayback();
                    }
                    clearInterval(t);
                }
            }, 10);
        }
        else {
            if (Animator) {
                this.initPlayback();
            }
        }

        if (typeof this.onLoadCallback === 'function') {
            this.onLoadCallback(this);
        }
    }

    getRootElement(): HTMLElement {
        if (this.inSandboxedIframe) {
            return this.ad.container;
        }
        else {
            return this.iframe.contentDocument!.body;
        }
    }

    /**
     * @summary
     * Check pixel visibilty.
     *
     * @description
     * The ability to render visible pixels on the banner. Notice, this API is different from
     * DOM visibility, where display none is still counted as visibly. We have manually confirmed
     * that display none:ed DOM subtree's are cheap to check bounding rect.
     */
    private isPixelVisible(): boolean {
        const testElement = this.document.createElement('div');
        testElement.style.height = '1px';
        testElement.style.width = '1px';
        testElement.style.position = 'absolute';
        testElement.style.top = '-100000px';
        this.ad.container.appendChild(testElement);
        const result = testElement.getBoundingClientRect().height > 0;
        this.ad.container.removeChild(testElement);
        return result;
    }

    private checkForWidgetElementNodes(): void {
        for (const element of this.renderer.viewTree.elements) {
            if (isWidgetNode(element)) {
                this.hasWidgetElements = true;
                break;
            }
        }
    }

    /**
     * Primitive responsiveness.
     */
    private onResize = (e?: Event) => {
        const scale = this.ad.responsive
            ? this.ad.container.clientWidth / this.creativeData.width
            : 1;
        this.iframe.contentWindow!.document.body.style.transform = `scale(${scale || 1})`;
    };

    private initPlayback(): void {
        const autoplay = this.ad.shouldAutoplay();
        this.animator.seek(this.getStartTime());

        if (this.ad.isOmniRender()) {
            // Map stop and pause for omniRender record logic
            // OmniRender stops recording when loop is emitted
            // and set loops to 1
            this.creativeData.loops = 1;
            this.animator.on('pause', _ => {
                if (!this.stoppedOmniRenderRecording) {
                    this.stoppedOmniRenderRecording = true;
                    this.animator.emit('loop');
                }
            });
        }
        // Play/pause creative when it leaves or enter view
        if (this.ad.viewability.pauseOutsideView()) {
            this.ad.on(AdEvents.ViewEnter, () => {
                this.animator.play();
            });
            this.ad.on(AdEvents.ViewLeave, () => {
                this.animator.pause();
            });
            if (this.ad.viewability.visible && autoplay) {
                this.animator.play();
            }
        }
        else if (autoplay) {
            this.animator.play();
        }

        this.ad.on(AdEvents.Stop, () => {
            this.animator.pause();
        });
    }

    /**
     * When user clicks the creative
     */
    private onClick = (e: MouseEvent, deepLinkUrl?: string) => {
        const url = this.getClickUrl();
        if (!url) {
            return;
        }

        if (deepLinkUrl) {
            this.ad.tracking.click({ x: e.x, y: e.y, deepLinkUrl });
        }
        else {
            this.ad.tracking.click(e);
        }
    };

    private getStartTime(): number {
        const seek = parseFloat(this.ad.parameters?.seek || '');

        // Seek parameter is added to the tag
        if (!isNaN(seek)) {
            return seek;
        }
        // Used by image generator to capture preload image at the right place in timeline
        if (this.shouldStartFromPreloadFrame()) {
            const preloadFrame = this.creativeData.preloadImage?.frames && this.creativeData.preloadImage.frames[0];

            // Defaults to 1 if not set
            return typeof preloadFrame === 'number' ? preloadFrame : 1;
        }
        else {
            return this.creativeData.startTime || 0;
        }
    }

    private shouldStartFromPreloadFrame(): boolean {
        return this.ad.parameters?.seek === 'preload-frame';
    }


    private setEnvironments(env?: IEnvironment): void {
        if (env) {
            this.env = env;
            this.env.ANIMATED_CREATIVE_JS = true;
            this.env.IN_IMAGE_GENERATOR = this.ad.parameters.env === 'image-generator' || this.shouldStartFromPreloadFrame();
            if (!isBannerflow(window.location.href)) {
                const origin = this.ad.getOrigin();
                if (origin) {
                    for (const e in this.env) {
                        if (!isUrl(this.env[e])) {
                            continue;
                        }
                        if (isRelativeUrl(this.env[e])) {
                            this.env[e] = concatUrl(origin, this.env[e]);
                        }
                        else {
                            this.env[e] = replaceOrigin(this.env[e], origin);
                        }
                    }
                }
            }
            WidgetRenderer && (WidgetRenderer.env = this.env);
        }
    }

    /**
     * Create wrapping iframe.
     * Note that in this stage we cannot insert content into the iframe in all browers (FF)
     */
    private createIframe(): HTMLIFrameElement {
        const iframe = document.createElement('iframe');
        // Dimension hacks to fix iframe issues in iOS:
        // https://stackoverflow.com/a/20142280
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        iframe.style.minWidth = '100%';
        iframe.style.minHeight = '100%';

        iframe.style.maxWidth = '100%';
        iframe.style.outline = 'none';
        iframe.style.border = 'none';
        iframe.style.opacity = '0';
        iframe.style.display = 'block';
        iframe.style.position = 'absolute';
        iframe.style.left = '0';
        iframe.style.top = '0';
        iframe.style.transformOrigin = '0 0';
        iframe.style.transform = 'none';
        iframe.style.filter = 'none';
        iframe.src = 'about:blank';
        iframe.scrolling = 'no';

        if (this.ad.shouldFadeIn()) {
            iframe.style.transition = 'opacity 0.2s ease';
        }
        this.ad.container.appendChild(iframe);

        return iframe;
    }

    /**
     * Needs to be executed AFTER the document is ready
     */
    private insertCreativeStyles(): void {

        const url = this.getClickUrl();

        let cursor = 'pointer;';
        if (!url || url === '#') {
            cursor = 'default;';
        }

        const contentDocument = this.inSandboxedIframe
            ? window.document
            : this.iframe.contentDocument!;

        const style = contentDocument.createElement('style');

        style.innerHTML = `
            * {
                box-sizing: border-box;
            }
            html, body {
                margin: 0;
                padding: 0;
                overflow: hidden;
                width: 100%;
                height: 100%;
                -ms-user-select: none;
                -moz-user-select: none;
                -webkit-user-select: none;
                user-select: none;
            }
            body {
                cursor: ${cursor};
            }`;

            contentDocument!.head!.appendChild(style);
    }

    private getClickUrl(): string | undefined {
        return this.ad.tracking.getRedirectUrl() || this.ad.tracking.getTargetUrl();
    }

    /**
     * When iframe window is ready
     * @param callback
     */
    private onIframeWindowLoad(iframeWindow: Window, callback: () => any): void {
        if (iframeWindow.document.readyState === 'complete') {
            setTimeout(() => {
                callback();
            }, 100);
        } else {
            const onLoad = () => {
                if (callback) {
                    callback();
                }
                if (iframeWindow) {
                    iframeWindow.removeEventListener('load', onLoad);
                }
            };
            iframeWindow.addEventListener('load', onLoad);
        }
    }

    private async createPreloadFont(fontStyle: IFontStyle, characters?: string): Promise<void> {
        const isInlined = fontStyle.src.startsWith('data');
        if (!isInlined && !isBannerflow(window.location.href)) {
            fontStyle.src = replaceOrigin(fontStyle.src, this.env.FONTSERVICE_API_ORIGIN!);
        }

        const fontface = `
        @font-face {
            font-family: "${fontStyle.id}";
            font-weight: ${fontStyle.weight};
            font-style: ${fontStyle.style};
            src: url("${fontStyle.src}") format("woff");
        }
        `;

        const fontFaceStyleNode = this.document.createElement('style');
        fontFaceStyleNode.id = fontStyle.id;
        fontFaceStyleNode.appendChild(
            this.document.createTextNode(fontface)
        );

        this.document.head.appendChild(fontFaceStyleNode);
        this.preloadedFonts.push(fontFaceStyleNode);
        if (isInlined) {
            return;
        }
        try {
            const font = new FontFaceObserver(fontStyle.id, {
                weight: fontStyle.weight,
                style: fontStyle.style
            }, this.iframe.contentWindow);
            await font.load(characters, 5000);
        }
        catch (e) {
            let msg = (typeof e === 'string') ? e : '';
            if (e instanceof Error) {
                msg = e.message || msg;
            }
            throw new Error(`Could not preload font ${fontStyle.id}, reason: ` + msg);
        }
    }

    addPreloadFontsToRenderedCreative(): void {
        for (const font of this.preloadedFonts) {
            this.document.head.appendChild(font);
        }
    }

    /** */
    destroy(): void {
        this.rootElement.removeEventListener('click', this.onClick);
        window.removeEventListener('resize', this.onResize);
        if (this.renderer) {
            this.renderer.destroy();
            delete this.renderer;
        }
        if (this.animator) {
            this.animator.destroy();
            delete this.animator;
        }
    }

    /**
     * Preloads all feeds as well as fonts that are used in conjunction as character styling
     */
    async preloadFeeds(): Promise<void> {
        this.creativeData.elements.forEach(child => {
            if (child.kind === NodeKind.Text) {
                for (const span of child.content.spans) {
                    if (span.type === SpanType.Variable) {
                        if (this.preloadedFeeds.indexOf(span.style.variable!.id) === -1) {
                            this.preloadedFeeds.push(span.style.variable!.id);
                        }
                    }
                }
            }
            else if (child.kind === NodeKind.Image) {
                if (child.feed) {
                    if (this.preloadedFeeds.indexOf(child.feed.id) === -1) {
                        this.preloadedFeeds.push(child.feed.id);
                    }
                }
            }
        });

        await this.feedStore.preloadFeeds(this.preloadedFeeds);
    }

    setChildUrls(): void {
        this.creativeData.elements.forEach(child => {
            if (child.kind === NodeKind.Image) {
                if (!child.feed) {
                    child.imageAsset!.url = this.getOptimizedImageUrl(child);
                }
            }
        });
    }

    private getOptimizedImageUrl(element: IImageElementDataNode): string {
        if (!element.imageAsset) {
            throw new Error(`Image asset is not defined on element '${element.id}'`);
        }

        const maxReducer = (max: number, keyframe) => keyframe.value > max ? keyframe.value as number : max;

        const width = Math.round((element.width as any[]).reduce<number>(maxReducer, 0));
        const height = Math.round((element.height as any[]).reduce<number>(maxReducer, 0));
        const imageUrl = element.imageAsset.url;
        const fitOption = element.fitOption ? element.fitOption[0].value : undefined;

        const originalWidth = element.imageAsset.width;
        const originalHeight = element.imageAsset.height;
        const imageOptimizerOrigin = this.env?.IMAGE_OPTIMIZER_ORIGIN;
        const q = element.quality ? (element.quality[0].value as number) : defaults.quality!;


        if (fitOption === 'cover' && originalWidth && originalHeight) {
            const fitPositionX = element.fitPositionX ? element.fitPositionX[0].value : 0;
            const fitPositionY = element.fitPositionY ? element.fitPositionY[0].value : 0;
            return this.cropImage(width, originalWidth, height, originalHeight, imageUrl, fitPositionX, fitPositionY, q);
        }
        return getImageOptimizerUrl(imageUrl, { width, height, fitOption, webpSupport: this.isWebPSupported, imageOptimizerOrigin, quality: q });
    }

    private cropImage(width: number, originalWidth: number, height: number, originalHeight: number, imageUrl: string, fitPositionX: number = 0, fitPositionY: number = 0, quality: number): string {
        const { newW, newH, scale } = this.getNewProportions(width, originalWidth, height, originalHeight);
        const imageOptimizerOrigin = this.env?.IMAGE_OPTIMIZER_ORIGIN;

        if (fitPositionX || fitPositionY) {

            const fitX = fitPositionX || 0;
            const fitY = fitPositionY || 0;

            const { percentX, percentY } = withinImageBounds(fitX, fitY, newW, width, newH, height);

            const maxY = Math.round(newH - height);
            const maxX = Math.round(newW - width);

            const position = {
                x: Math.round(percentX * maxX) || 0,
                y: Math.round(percentY * maxY) || 0
            };

            return getImageOptimizerUrl(imageUrl, { width, height, webpSupport: this.isWebPSupported, method: 'optimizepositionedimage', position, imageOptimizerOrigin,  quality });
        }

        const x1 = Math.round(((newW - width) / scale) / 2);
        const y1 = Math.round(((newH - height) / scale) / 2);
        const x2 = Math.round(originalWidth - x1);
        const y2 = Math.round((originalHeight - y1));

        return getImageOptimizerUrl(imageUrl, { width, height, webpSupport: this.isWebPSupported, cropping: { x1, x2, y1, y2 }, imageOptimizerOrigin, quality });
    }



    private getNewProportions = (elementWidth: number, originalWidth: number, elementHeight: number, originalHeight: number) => {
        let newW = originalWidth;
        let newH = originalHeight;

        const scale = Math.max(elementWidth / originalWidth, elementHeight / originalHeight);

        newW = Math.round(newW * scale);
        newH = Math.round(newH * scale);
        return { newW, newH, scale };
    }

    async preloadFonts(): Promise<void> {
        if (this.feedStore) {
            this.renderer.viewTree.elements.forEach(child => {
                if (isTextNode(child)) {
                    for (const span of child.content.spans) {
                        if (span.type === SpanType.Variable) {
                            this.feedStore.addFeedElement(child.id, span.style.variable as IFeed, child);
                        }
                    }
                }
            });

            for (const id of this.preloadedFeeds) {
                // Adds to fontCharacterCache and uses preload below if in output
                this.feedStore.loadFontsBasedOnFeedData(id, true);
            }
        }

        const promises: Promise<void>[] = [];
        for (const font of this.preloadAssets.fonts) {
            if (font.src.startsWith('data')) {
                promises.push(this.createPreloadFont(font));
            }
            else {
                const [url, encodedChars] = font.src.split(FONT_SERVICE_TEXT_PARAMETER);
                const feedCharacters = (this.feedStore && this.feedStore.fontCharacterCache.get(font.id)) || '';
                const textCharacters = decodeURIComponent(encodedChars);
                const { textParams, distinctText }: IFontServiceParams = getFontServiceParamsFromString(feedCharacters + textCharacters);
                if (feedCharacters) {
                    this.feedStore.fontCharacterCache.set(font.id, distinctText);
                    font.src = url + textParams;
                }
                promises.push(this.createPreloadFont(font, distinctText));
            }
        }
        await Promise.all(promises);
    }
}

export { Color } from '../creative/color';
